import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DataGridPro,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useSearchParams } from 'react-router-dom'
import { useUserContext } from '../../Contexts/UserConfig'
import { useMessagePopupContext } from '../../garvan-react/Components/ModalFeedback/MessagePopup'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AxiosResponse } from 'axios'
import LimsService from '../../APIs/Python/APIs/LimsService'
import PageSection from '../../Constants&Theme/PageSection'
import { appBar } from '../../garvan-react/Constants/StyleConstants'
import { usePricebookCols } from './PricebookCols'
import { stackGap } from '../../Constants&Theme/Spacing'
import {
  AttachMoney,
  Cancel,
  CurrencyExchange,
  Percent,
  PriceChange,
} from '@mui/icons-material'
import PyAPI from '../../APIs/Python/APIs/PyAPI'
import { LoadingButton } from '@mui/lab'
import PricebookToolbar from './PricebookToolbar'

export const categories = [
  'Holding',
  'Issue',
  'Service',
  'Shipper',
  'Shipping',
  'Tissue',
]

export const facilityRateCodes: { [key: string]: any } = {
  1: {
    facility: 'abr',
    long: 'ABR bills Garvan, G2 or SRI',
    short: 'Garvan, G2 or SRI',
  },
  4: {
    facility: 'abr',
    long: 'ABR bills Facilities of type Pre-opening partner, SVH or VCC',
    short: 'Pre-opening partners',
  },
  8: {
    facility: 'abr',
    long: 'ABR bills Facilities of type External, MON or "GST - non applicable"',
    short: 'Externals, MON, Non-GST',
  },
  16: {
    facility: 'garvan',
    long: 'Garvan bills Garvan',
    short: 'Garvan',
  },
  32: {
    facility: 'garvan',
    long: 'Garvan bills Pre-opening partner, Post-opening partner, VCCRI, SVH',
    short: 'Partners, VCCRI, SVH',
  },
  64: {
    facility: 'garvan',
    long: 'Garvan bills MON, External',
    short: 'Externals, MON',
  },
  128: {
    facility: 'abr',
    long: 'Externals for Profit',
    short: 'Externals for Profit',
  },
  256: {
    facility: 'garvan',
    long: 'Externals for Profit',
    short: 'Externals for Profit',
  },
}

function Pricebook() {
  const { whoAmI } = useUserContext()
  const queryClient = useQueryClient()
  const { setError } = useMessagePopupContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [rateCodeFilter, setRateCodeFilter] = useState({})
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([])
  const [incType, setIncType] = useState('$')
  const [incAmount, setIncAmount] = useState('')
  const category = searchParams.get('category')
  const facility = searchParams.get('facility')
  const pricebookQuery = useQuery<any, AxiosResponse>({
    queryKey: ['LimsService', 'getPricebook', category],
    queryFn: () => LimsService.getPricebook(whoAmI!, category!.toLowerCase()!),
    onError: (err) => setError(err),
    enabled: !!whoAmI && !!category,
  })
  const { mutate: updateRates, isLoading } = useMutation<any, AxiosResponse>(
    () =>
      PyAPI.post(
        whoAmI,
        `/pricebook/${category!.toLowerCase()!}`,
        pricebookChanges,
      ),
    {
      onError: (err) => setError(err),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['LimsService', 'getPricebook', category],
        })
        setPricebookChanges({})
      },
    },
  )

  const [pricebookChanges, setPricebookChanges] = useState<any>({})

  const rows = useMemo(() => {
    return (pricebookQuery.data ?? []).map((row: any) => {
      const id = row[`${category}RateID`]
      return {
        ...row,
        id,
        NewRate: pricebookChanges?.[id],
      }
    })
  }, [pricebookQuery, pricebookChanges, category])
  
  const columns: any[] = usePricebookCols(category)
  const apiRef = useGridApiRef()
  const showRateCodes = Object.entries(rateCodeFilter ?? {})
    .filter(([key, val]) => val !== false)
    .map(([key, val]) => key)
  return (
    <Box maxWidth={'lg'}>
      <PageSection title={'Pricebook'}>
        <Stack
          direction={'row'}
          width={1}
          gap={stackGap.row}
          sx={{ mb: 2 }}
        >
          <FormControl
            fullWidth
            disabled={!!Object.keys(pricebookChanges).length}
          >
            <InputLabel>Select Rate Category</InputLabel>
            <Select
              inputProps={{ 'aria-label': 'Select Rate Category' }}
              label="Select Rate Category"
              value={category ?? ''}
              onChange={(e) => {
                setSearchParams((params) => {
                  params.set('category', e.target.value!)
                  if (e.target.value === 'Shipper') {
                    params.delete('facility')
                  }
                  return params
                })
                if (e.target.value === 'Shipper') {
                  setRateCodeFilter({})
                }
              }}
            >
              {categories.map((item) => (
                <MenuItem
                  value={item}
                  key={item}
                >
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ButtonGroup color="primary">
            <Tooltip
              title={
                <Typography>
                  Adjust all selected selected by <strong>percentage</strong>{' '}
                  amount
                </Typography>
              }
            >
              <Button
                size="small"
                variant={incType === '%' ? 'contained' : 'outlined'}
                onClick={() => setIncType('%')}
                sx={{ width: 50 }}
              >
                <Percent fontSize={'small'} />
              </Button>
            </Tooltip>
            <Tooltip
              title={
                <Typography>
                  <strong>Adjust</strong> all selected selected by entered{' '}
                  amount
                </Typography>
              }
            >
              <Button
                size="small"
                variant={incType === '$' ? 'contained' : 'outlined'}
                onClick={() => setIncType('$')}
                sx={{ width: 50 }}
              >
                <AttachMoney fontSize={'small'} />
              </Button>
            </Tooltip>
            <Tooltip
              title={
                <Typography>
                  <strong>Set</strong> all selected selected to entered amount
                </Typography>
              }
            >
              <Button
                size="small"
                variant={incType === '=' ? 'contained' : 'outlined'}
                onClick={() => setIncType('=')}
                sx={{ width: 50, fontSize: '1.5em' }}
              >
                =
              </Button>
            </Tooltip>
          </ButtonGroup>
          <FormControl
            sx={{ minWidth: 120 }}
            variant="outlined"
          >
            <OutlinedInput
              startAdornment={incType !== '%' ? '$' : ''}
              endAdornment={incType === '%' ? '%' : ''}
              value={incAmount}
              onChange={(e) => {
                const val = e.target.value
                if (/^-?[0-9]*\.?[0-9]*$/.test(val)) {
                  setIncAmount(val)
                }
              }}
              placeholder={incType === '=' ? ' Set Value' : incAmount}
            />
          </FormControl>
          <Button
            variant="outlined"
            disabled={!rowSelectionModel.length || !incAmount}
            onClick={() => {
              rowSelectionModel.forEach((id) => {
                setPricebookChanges((p: any) => {
                  const oldRate = (pricebookQuery.data ?? []).find(
                    (row: any) => row[`${category}RateID`] === id,
                  ).Rate
                  let newRate
                  if (incType === '%') {
                    newRate = Math.max(
                      parseFloat(oldRate) * (1 + parseFloat(incAmount) / 100),
                      0.01,
                    )
                  } else if (incType === '$') {
                    newRate = Math.max(parseFloat(oldRate) + 0.01)
                  } else {
                    newRate = parseFloat(incAmount)
                  }
                  return {
                    ...p,
                    [id]: (Math.round(newRate * 100) / 100).toString(), //round to 2 decimal places,
                  }
                })
                setRowSelectionModel([])
              })
            }}
            sx={{ minWidth: 120 }}
            startIcon={<PriceChange />}
          >
            Adjust Rate
          </Button>
          <LoadingButton
            variant="contained"
            color="success"
            disabled={!Object.keys(pricebookChanges).length}
            loading={isLoading}
            onClick={() => updateRates()}
            sx={{ minWidth: 160 }}
            startIcon={<CurrencyExchange />}
          >
            {`Save ${Object.keys(pricebookChanges).length} Changes`}
          </LoadingButton>
          <Button
            variant="outlined"
            disabled={!Object.keys(pricebookChanges).length}
            onClick={() => setPricebookChanges({})}
            sx={{ minWidth: 100 }}
            startIcon={<Cancel />}
            color="warning"
          >
            Clear
          </Button>
        </Stack>
        <TableContainer
          sx={{
            height: `calc(100vh - ${appBar}px - 300px)`,
            width: 1,
            '& .new-rate': {
              fontWeight: '600',
            },
          }}
        >
          <DataGridPro
            apiRef={apiRef}
            rows={
              rows.filter((r: any) => {
                if (showRateCodes.length) {
                  return Object.entries(facilityRateCodes)
                    .filter(([key, val]) =>
                      showRateCodes.includes(key.toString()),
                    )
                    .map(([key, val]) => key)
                    .includes(r?.RateCode?.toString())
                } else if (facility) {
                  return Object.entries(facilityRateCodes)
                    .filter(([key, val]) => val.facility === facility)
                    .map(([key, val]) => key)
                    .includes(r?.RateCode?.toString())
                } else {
                  return true
                }
              }) ?? []
            }
            columns={columns}
            loading={pricebookQuery.isFetching}
            checkboxSelection={true}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel)
            }}
            slots={{
              toolbar: PricebookToolbar as any,
            }}
            slotProps={{
              toolbar: {
                facility,
                rateCodeFilter,
                setRateCodeFilter,
                category,
              },
            }}
            rowSelectionModel={rowSelectionModel}
            initialState={{
              pinnedColumns: {
                right: ['RateCode', 'Rate', 'NewRate'],
              },
              columns: {
                columnVisibilityModel: {
                  AuditID: false,
                  HoldingRateID: false,
                  IssueRateID: false,
                  ServiceRateID: false,
                  ShippingRateID: false,
                },
              },
            }}
          />
        </TableContainer>
      </PageSection>
    </Box>
  )
}

export default Pricebook
